/**
 * gridOverlay.ts — pure geometry for the CAD-style grid overlay (Slice 3).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  LOCKED 2026-06-07 (memory `reference_grid_cad_overlay_spec`). This is a
 *  FAITHFUL PORT of the owner-approved v7 preview script
 *  (`Owner's Inbox/04_Projects/AVBP/CAD_GEOMETRY_OptionC_v7_2026-06-07.html`).
 *  Same boundary in → same overlay out. DO NOT redesign — it took ~8 preview
 *  rounds to lock the look.
 *
 *  WHAT THIS BUILDS (all in IMAGE-PIXEL space, same as the boundary + grid):
 *   - cleanBoundary()      — strip near-duplicate vertices (fixes the S3 artefact)
 *   - hitFar()             — FARTHEST boundary hit along a ray (notch-safe mark placement)
 *   - edgeNormals()/miter()— outward-normal per wall (inside/outside test so a
 *                            concave wall doesn't flip inward) + per-vertex mitre
 *   - zoneStrip()          — the thin edge-parallel colour strip JUST OUTSIDE the
 *                            boundary, split at the 16 zone-divider rays, each
 *                            segment its zone colour (preview `ring()`/`omitAt()`).
 *   - radialLabel()        — an outside label anchored to the FARTHEST hit, rotated
 *                            radially along the spoke (preview `rlabel`).
 *   - dividerTick()        — a bracket tick beyond the strip (preview `divtick`).
 *   - overlayMetrics()     — the diag-derived offsets (STRIP/BREAK/PAD_LBL/…).
 *
 *  The React component (`FloorBoundaryTracer.tsx`) consumes these and emits SVG.
 *  Colour arrays/maps live in `padaColours.ts`. The 32-ray ring L + centroid
 *  come from `vastuGrid.ts` (the engine-parity source of truth) — reused, not
 *  re-derived.
 *
 *  PURE — no DOM, no Firebase. Unit-tested (`test/gridOverlay.test.cjs`):
 *  outward-normal/strip-offset direction, hitFar (farthest not nearest), dedupe.
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { Pt } from './vastuGrid';

/* ═══════════════════════ ray + polygon primitives (verbatim) ═══════════════════════ */

/** Compass ray direction (deg) → unit {dx,dy} in image space (y down). */
export function rayDir(deg: number): { dx: number; dy: number } {
  const r = (deg * Math.PI) / 180;
  return { dx: Math.sin(r), dy: -Math.cos(r) };
}

/**
 * Ray (cx,cy)+t·(dx,dy) ∩ segment p1→p2. Returns the hit with its t (distance
 * along the ray) or null. Verbatim of the preview `raySeg`.
 */
export function raySeg(
  cx: number,
  cy: number,
  dx: number,
  dy: number,
  p1: Pt,
  p2: Pt,
): { x: number; y: number; t: number } | null {
  const sx = p2.x - p1.x;
  const sy = p2.y - p1.y;
  const den = dx * -sy - dy * -sx;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((p1.x - cx) * -sy - (p1.y - cy) * -sx) / den;
  const s = (dx * (p1.y - cy) - dy * (p1.x - cx)) / den;
  if (t >= 0 && s >= -1e-6 && s <= 1 + 1e-6) {
    return { x: cx + t * dx, y: cy + t * dy, t };
  }
  return null;
}

/**
 * FARTHEST boundary hit along a ray from (cx,cy) at compass `deg` — used to
 * place outside marks beyond the OUTERMOST wall, so a notch never pulls a mark
 * inward. Verbatim of the preview `hitFar` (takes max-t, not min-t). Falls back
 * to a fixed 100px reach if the ray misses (degenerate boundary).
 */
export function hitFar(cx: number, cy: number, deg: number, b: Pt[]): Pt {
  const { dx, dy } = rayDir(deg);
  let best: { x: number; y: number; t: number } | null = null;
  for (let k = 0; k < b.length; k++) {
    const h = raySeg(cx, cy, dx, dy, b[k], b[(k + 1) % b.length]);
    if (h && (!best || h.t > best.t)) best = h;
  }
  return best ? { x: best.x, y: best.y } : { x: cx + dx * 100, y: cy + dy * 100 };
}

/** Even-odd point-in-polygon (preview `pointInPoly`). */
export function pointInPoly(p: Pt, poly: Pt[]): boolean {
  let ins = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;
    const it =
      yi > p.y !== yj > p.y &&
      p.x < ((xj - xi) * (p.y - yi)) / (yj - yi || 1e-12) + xi;
    if (it) ins = !ins;
  }
  return ins;
}

/** Compass angle (deg, 0..360) of point p as seen from (cx,cy). Preview `compassAngle`. */
export function compassAngle(cx: number, cy: number, p: Pt): number {
  let a = (Math.atan2(p.x - cx, -(p.y - cy)) * 180) / Math.PI;
  if (a < 0) a += 360;
  return a;
}

/* ═══════════════════════ boundary cleaning (S3 artefact fix) ═══════════════════════ */

/**
 * Strip near-duplicate vertices (< `tol` px apart) and drop a trailing point
 * coincident with the first, then force CLOCKWISE winding (signed area < 0 →
 * reverse). Verbatim of the preview's `B` construction. Fixes the S3 jut from
 * the duplicate `{x:395,y:1544}/{x:395,y:1543}` pair.
 */
export function cleanBoundary(raw: Pt[], tol = 3): Pt[] {
  const B: Pt[] = [];
  for (const p of raw) {
    const last = B[B.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > tol) B.push(p);
  }
  if (
    B.length > 1 &&
    Math.hypot(B[0].x - B[B.length - 1].x, B[0].y - B[B.length - 1].y) < tol
  ) {
    B.pop();
  }
  let s = 0;
  for (let i = 0; i < B.length; i++) {
    const j = (i + 1) % B.length;
    s += B[i].x * B[j].y - B[j].x * B[i].y;
  }
  if (s < 0) B.reverse();
  return B;
}

/* ═══════════════════════ outward normals + mitre ═══════════════════════ */

export interface EdgeNormal {
  nx: number;
  ny: number;
}

/**
 * Per-wall OUTWARD unit normal. The raw normal is flipped to point OUT of the
 * polygon using a `pointInPoly` test on the wall midpoint nudged along it — so
 * a concave wall's normal does NOT flip inward. Verbatim of the preview `edgeN`.
 */
export function edgeNormals(B: Pt[]): EdgeNormal[] {
  const n = B.length;
  const out: EdgeNormal[] = [];
  for (let i = 0; i < n; i++) {
    const a = B[i];
    const b = B[(i + 1) % n];
    let dx = b.x - a.x;
    let dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;
    let nx = -dy;
    let ny = dx;
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    if (pointInPoly({ x: mid.x + nx * 2, y: mid.y + ny * 2 }, B)) {
      nx = -nx;
      ny = -ny;
    }
    out.push({ nx, ny });
  }
  return out;
}

export interface MiterInfo {
  /** mitre direction (unit) at vertex i */
  dir: Pt;
  /** mitre cos factor (≥0.25 clamp) — divide the offset by this to keep width constant */
  cos: number;
}

/**
 * Per-vertex mitre = bisector of the two adjacent edge normals, with the
 * cos clamp so a sharp corner doesn't explode the offset. Verbatim of the
 * preview `mdir`/`mcos`.
 */
export function miters(B: Pt[], edgeN: EdgeNormal[]): MiterInfo[] {
  const n = B.length;
  const out: MiterInfo[] = [];
  for (let i = 0; i < n; i++) {
    const n1 = edgeN[(i - 1 + n) % n];
    const n2 = edgeN[i];
    let mx = n1.nx + n2.nx;
    let my = n1.ny + n2.ny;
    const ml = Math.hypot(mx, my) || 1;
    mx /= ml;
    my /= ml;
    let cos = mx * n2.nx + my * n2.ny;
    if (cos < 0.25) cos = 0.25;
    out.push({ dir: { x: mx, y: my }, cos });
  }
  return out;
}

/** Offset vertex i outward by `d` along its mitre (preview `omitAt`). */
export function miterOffset(
  B: Pt[],
  miterArr: MiterInfo[],
  i: number,
  d: number,
): Pt {
  const m = miterArr[i];
  return { x: B[i].x + (m.dir.x * d) / m.cos, y: B[i].y + (m.dir.y * d) / m.cos };
}

/* ═══════════════════════ zone colour strip (edge-parallel) ═══════════════════════ */

export interface StripQuad {
  /** the 4 corners of one strip segment: [innerA, innerB, outerB, outerA] */
  poly: Pt[];
  /** the resolved fill colour for this segment */
  col: string;
}

/**
 * Build the edge-parallel colour strip JUST OUTSIDE the boundary. Each wall is
 * offset outward (inner→outer band), SPLIT where a divider ray crosses it, and
 * each sub-segment is coloured by `colFn(compassAngleOfMidpoint)`. Mitred at
 * corners via `omitAt`. Verbatim of the preview `ring()` — here specialised to
 * the 16 ZONE dividers (no pada strip per the locked spec).
 *
 * @param B        cleaned, CW boundary
 * @param centroid grid centroid (ray origin for the divider crossings)
 * @param innerOff inner offset from the wall (0 = on the boundary)
 * @param outerOff outer offset (the strip width = outerOff - innerOff)
 * @param divs     absolute divider-ray compass degrees (the 16 zone dividers)
 * @param colFn    midpoint compass angle → fill colour
 */
export function zoneStrip(
  B: Pt[],
  centroid: Pt,
  innerOff: number,
  outerOff: number,
  divs: number[],
  colFn: (ang: number) => string,
): StripQuad[] {
  const n = B.length;
  const edgeN = edgeNormals(B);
  const miterArr = miters(B, edgeN);
  const quads: StripQuad[] = [];
  const C = centroid;
  for (let i = 0; i < n; i++) {
    const a = B[i];
    const b = B[(i + 1) % n];
    const na = edgeN[i];
    // collect every divider-ray crossing of this wall, ordered along it
    const cs: { t: number; p: Pt }[] = [];
    for (const d of divs) {
      const { dx, dy } = rayDir(d);
      const h = raySeg(C.x, C.y, dx, dy, a, b);
      if (h) {
        const t =
          ((h.x - a.x) * (b.x - a.x) + (h.y - a.y) * (b.y - a.y)) /
          ((b.x - a.x) ** 2 + (b.y - a.y) ** 2 || 1);
        if (t > 1e-3 && t < 1 - 1e-3) cs.push({ t, p: { x: h.x, y: h.y } });
      }
    }
    cs.sort((u, v) => u.t - v.t);
    // node chain a → crossings → b, each with its inner/outer offset point
    const nodes: { p: Pt; inner: Pt; outer: Pt }[] = [
      { p: a, inner: miterOffset(B, miterArr, i, innerOff), outer: miterOffset(B, miterArr, i, outerOff) },
    ];
    for (const c of cs) {
      nodes.push({
        p: c.p,
        inner: { x: c.p.x + na.nx * innerOff, y: c.p.y + na.ny * innerOff },
        outer: { x: c.p.x + na.nx * outerOff, y: c.p.y + na.ny * outerOff },
      });
    }
    nodes.push({
      p: b,
      inner: miterOffset(B, miterArr, (i + 1) % n, innerOff),
      outer: miterOffset(B, miterArr, (i + 1) % n, outerOff),
    });
    for (let k = 0; k < nodes.length - 1; k++) {
      const A = nodes[k];
      const Bp = nodes[k + 1];
      const mid = { x: (A.p.x + Bp.p.x) / 2, y: (A.p.y + Bp.p.y) / 2 };
      const col = colFn(compassAngle(C.x, C.y, mid));
      quads.push({ poly: [A.inner, Bp.inner, Bp.outer, A.outer], col });
    }
  }
  return quads;
}

/* ═══════════════════════ labels + ticks (radial, notch-safe) ═══════════════════════ */

export interface RadialLabel {
  x: number;
  y: number;
  /** rotation degrees (along the spoke) to apply about (x,y) */
  rotate: number;
}

/**
 * Place a label OUTSIDE the boundary along compass `deg`, anchored to the
 * FARTHEST boundary hit (notch-safe) then pushed out by `off`, rotated radially
 * (along the spoke, no reading-flip). Verbatim placement of the preview
 * `rlabel` (the component supplies text/size/colour). Returns geometry only.
 */
export function radialLabel(
  centroid: Pt,
  deg: number,
  off: number,
  B: Pt[],
): RadialLabel {
  const h = hitFar(centroid.x, centroid.y, deg, B);
  const { dx, dy } = rayDir(deg);
  const px = h.x + dx * off;
  const py = h.y + dy * off;
  const rotate = (Math.atan2(dy, dx) * 180) / Math.PI;
  return { x: px, y: py, rotate };
}

export interface TickLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * A bracket tick along the ray for grid-divider index `i` (1..32), running from
 * `break` to `len` beyond the FARTHEST boundary hit. Verbatim of the preview
 * `divtick` placement (the component supplies stroke/width). `rotation` is the
 * grid north rotation (deg).
 */
export function dividerTick(
  centroid: Pt,
  i: number,
  rotation: number,
  breakOff: number,
  len: number,
  B: Pt[],
): TickLine {
  const deg = ((i - 1) * 11.25 + rotation + 360) % 360;
  const p = hitFar(centroid.x, centroid.y, deg, B);
  const { dx, dy } = rayDir(deg);
  return {
    x1: p.x + dx * breakOff,
    y1: p.y + dy * breakOff,
    x2: p.x + dx * len,
    y2: p.y + dy * len,
  };
}

/* ═══════════════════════ divider-ray degree sets + metrics ═══════════════════════ */

/** The 16 ZONE-divider absolute compass degrees (preview `zoneDivs`). */
export function zoneDividerDegs(rotation: number): number[] {
  const out: number[] = [];
  for (let m = 0; m < 16; m++) out.push((m * 22.5 + 11.25 + rotation) % 360);
  return out;
}

/** Which 16-zone index (0..15) does a compass angle fall in, given north rotation. */
export function zoneOfAngle(ang: number, rotation: number): number {
  const a = ((ang - rotation) % 360 + 360) % 360;
  return Math.floor((a + 11.25) / 22.5) % 16;
}

/** Which pada index (1..32) does a compass angle fall in, given north rotation. */
export function padaOfAngle(ang: number, rotation: number): number {
  const a = ((ang - rotation) % 360 + 360) % 360;
  return (Math.floor(a / 11.25) % 32) + 1;
}

export interface OverlayMetrics {
  diag: number;
  STRIP: number;
  BREAK: number;
  PAD_LBL: number;
  PAD_END: number;
  ZON_LBL: number;
  ZON_END: number;
  /** pada-label font size (image-pixel units) — diag-relative, see below */
  PAD_FS: number;
  /** zone-label font size (image-pixel units) — diag-relative, see below */
  ZON_FS: number;
}

/**
 * The diag-derived offsets, verbatim of the preview constants. `diag` is the
 * bounding-box diagonal of the cleaned boundary. All offsets are fractions of
 * it, so the overlay scales with the plan.
 *
 * BUG B FIX (2026-06-08): the v7 preview hard-coded the pada/zone label font
 * sizes at 26 / 40 — but those were tuned for the reference plot whose diag was
 * ~3012px (IMG 1260×2736). The React port copied the literal 26 / 40 into the
 * <text> without scaling, so on a SMALL boundary (e.g. a degenerate 3-point
 * near-triangle) the labels became enormous relative to the plan — the "giant
 * overlapping coloured letters" the owner saw. Restored as diag-relative
 * fractions matching the preview EXACTLY at the reference diag:
 *   26 / 3012.19 ≈ 0.008632 · diag  (pada)
 *   40 / 3012.19 ≈ 0.013279 · diag  (zone)
 * So the look is byte-identical on the reference plot and stays proportional on
 * any other size — no more garble on a tiny or partial trace.
 */
export const PAD_FONT_FRAC = 0.008632;
export const ZON_FONT_FRAC = 0.013279;

export function overlayMetrics(B: Pt[]): OverlayMetrics {
  let minx = Infinity;
  let miny = Infinity;
  let maxx = -Infinity;
  let maxy = -Infinity;
  for (const p of B) {
    if (p.x < minx) minx = p.x;
    if (p.y < miny) miny = p.y;
    if (p.x > maxx) maxx = p.x;
    if (p.y > maxy) maxy = p.y;
  }
  const diag = Math.hypot(maxx - minx, maxy - miny) || 1;
  return {
    diag,
    STRIP: 0.024 * diag,
    BREAK: 0.052 * diag,
    PAD_LBL: 0.08 * diag,
    PAD_END: 0.066 * diag,
    ZON_LBL: 0.145 * diag,
    ZON_END: 0.15 * diag,
    PAD_FS: PAD_FONT_FRAC * diag,
    ZON_FS: ZON_FONT_FRAC * diag,
  };
}
